import { useRef, useCallback, useEffect } from 'react';
import { resolveAddress } from '../../../../utils/geocodeEdge';

/**
 * Geokodowanie pojedynczego adresu (np. widok szczegółów eventu):
 * najpierw współrzędne z rekordu, potem localStorage, na końcu Edge Function.
 * Mapa Discover nie używa już batch geocode — tylko latitude/longitude z bazy.
 */
export function useGeocoding() {
  const geocodeCacheRef = useRef({});

  const getEventCoordinates = useCallback((event) => {
    if (!event) return null;
    const latRaw = event.latitude ?? event.lat ?? event.coordinates?.lat;
    const lngRaw = event.longitude ?? event.lng ?? event.lon ?? event.coordinates?.lng;
    const lat = Number(latRaw);
    const lng = Number(lngRaw);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng };
    }
    return null;
  }, []);

  useEffect(() => {
    try {
      const cached = localStorage.getItem('geocodeCache');
      if (cached) {
        geocodeCacheRef.current = JSON.parse(cached);
      }
    } catch (err) {
      console.warn('Failed to load geocode cache from localStorage:', err);
    }
  }, []);

  const saveCacheToStorage = useCallback(() => {
    try {
      localStorage.setItem('geocodeCache', JSON.stringify(geocodeCacheRef.current));
    } catch (err) {
      console.warn('Failed to save geocode cache to localStorage:', err);
    }
  }, []);

  const geocodeSingleEvent = useCallback(
    async (event) => {
      if (!event?.location) {
        const coordsFromEvent = getEventCoordinates(event);
        if (!coordsFromEvent) return null;
        return {
          ...event,
          coordinates: coordsFromEvent,
        };
      }

      const coordsFromEvent = getEventCoordinates(event);
      if (coordsFromEvent) {
        return {
          ...event,
          coordinates: coordsFromEvent,
        };
      }

      if (geocodeCacheRef.current[event.location]) {
        const cached = geocodeCacheRef.current[event.location];
        return {
          ...event,
          coordinates: { lat: cached[0], lng: cached[1] },
        };
      }

      try {
        const result = await resolveAddress(event.location);
        if (result && result.lat != null && result.lng != null) {
          geocodeCacheRef.current[event.location] = [result.lat, result.lng];
          saveCacheToStorage();
          return {
            ...event,
            coordinates: { lat: result.lat, lng: result.lng },
          };
        }
      } catch (err) {
        console.warn(`Error geocoding ${event.location}:`, err);
      }

      return null;
    },
    [getEventCoordinates, saveCacheToStorage]
  );

  return {
    geocodeSingleEvent,
  };
}

export default useGeocoding;
